import { motion } from 'framer-motion';
import { Building2, Factory, Zap, Settings, Network, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../components/AnimatedSection';

const Solutions = () => {
  const solutions = [
    {
      icon: <Building2 className="w-12 h-12" />,
      title: 'Smart, Connected Buildings',
      subtitle: 'Building Automation Systems (BAS)',
      description: 'EWSS Automation designs and implements comprehensive Building Automation Systems that integrate all building services into one intelligent control platform. From commercial offices to large hotels, we deliver seamless control of every building function.',
      features: [
        'HVAC control & optimization',
        'Lighting management & scheduling',
        'Access control & security integration',
        'Fire & safety system integration',
        'Energy monitoring & reporting',
        'Centralised building management dashboards',
        'Wireless & wired sensor networks',
        'Retrofit & new-build solutions'
      ],
      enabledBy: ['Haiwell', 'Yotta Control', 'EWSS I/O Modules'],
      image: 'https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/an6rre3w_BMS%20Africa.jpg'
    },
    {
      icon: <Factory className="w-12 h-12" />,
      title: 'Intelligent Manufacturing & Process Control',
      subtitle: 'Industrial Automation Solutions',
      description: 'With 30 years of hands-on experience, EWSS Automation designs, implements and manages complete industrial automation systems — from single-machine PLC control to plant-wide SCADA and MES integration.',
      features: [
        'PLC programming & commissioning',
        'HMI / SCADA system design',
        'Process control & instrumentation',
        'Motor drive & motion control systems',
        'Remote monitoring & diagnostics',
        'ERP / MES integration',
        'Retrofit of legacy control systems',
        'Factory acceptance testing (FAT)'
      ],
      enabledBy: ['Haiwell', 'Savch', 'Supmea'],
      image: 'https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/z9gg9qad_Industrial%20Automation.jpg'
    },
    {
      icon: <Zap className="w-12 h-12" />,
      title: 'Monitor, Optimise & Reduce',
      subtitle: 'Energy Management Solutions',
      description: 'EWSS Automation helps organisations take control of their energy consumption through real-time monitoring, data analytics and automated demand management — reducing costs and supporting sustainability goals.',
      features: [
        'Energy metering & sub-metering',
        'Power quality monitoring',
        'Demand-side management (DSM)',
        'Solar & renewable integration monitoring',
        'Automated load shedding & control',
        'Energy dashboards & reporting',
        'ISO 50001 support',
        'Utility billing verification'
      ],
      enabledBy: ['Haiwell', 'Supmea', 'EWSS I/O Modules'],
      image: 'https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/6i80m913_Energy%20Eficiency.jpg'
    },
    {
      icon: <Settings className="w-12 h-12" />,
      title: 'Connected Systems, Actionable Intelligence',
      subtitle: 'IoT Integration & Smart Technology',
      description: 'EWSS Automation bridges the gap between operational technology (OT) and information technology (IT) — connecting plant-floor devices, sensors and systems to cloud platforms for real-time visibility and intelligent decision-making.',
      features: [
        'IIoT architecture design & deployment',
        'Modbus, MQTT & OPC-UA integration',
        '4G / LoRa / Wi-Fi wireless sensor networks',
        'Cloud SCADA & remote access platforms',
        'Edge computing & IoT gateways',
        'Mobile monitoring applications',
        'Predictive maintenance enablement',
        'Third-party system API integration'
      ],
      enabledBy: ['Haiwell', 'Yotta Control', 'EWSS I/O Modules'],
      image: 'https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/ygfxwg2g_System%20Intergration.jpg'
    },
    {
      icon: <Network className="w-12 h-12" />,
      title: 'Smart Distributed I/O',
      subtitle: 'Yotta Control I/O & Edge Connectivity',
      description: 'Deploy distributed I/O across your plant using RS485, WiFi, Ethernet or daisy-chain networks. EWSS Automation integrates Yotta Control Smart I/O with existing PLC/SCADA systems to dramatically reduce wiring cost, simplify commissioning, and bring edge data into the cloud — the backbone of modern Industrial IoT.',
      features: [
        'RS485, WiFi, Ethernet & daisy-chain I/O options',
        'Digital, analog & mixed-signal modules',
        'Modbus RTU / TCP & MQTT protocol support',
        'Hot-swappable modular architecture',
        'Edge-computing ready (A-9N Fanless Edge)',
        'IoT gateway integration (9101 / 9103)',
        'Remote diagnostics & firmware updates',
        'Industrial-grade reliability (-20°C to +70°C)'
      ],
      enabledBy: ['Yotta Control'],
      image: 'https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/vmyxxe5g_Yotta%20Controls%20Pic.jpg'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-ewss-navy via-ewss-navy to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Our <span className="text-ewss-cyan">Solutions</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Comprehensive automation solutions backed by 30+ years of combined experience
            </p>
          </motion.div>
        </div>
      </section>

      {/* Solutions Detail */}
      <section className="py-20 bg-ewss-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {solutions.map((solution, index) => (
              <AnimatedSection key={index}>
                <div className={`flex flex-col ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                } gap-12 items-center`}>
                  <div className="flex-1">
                    <div className="aspect-video rounded-xl overflow-hidden border border-ewss-cyan/30">
                      <img 
                        src={solution.image}
                        alt={solution.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>

                  <div className="flex-1">
                    <div className="w-20 h-20 bg-ewss-cyan/10 rounded-xl flex items-center justify-center text-ewss-cyan mb-6">
                      {solution.icon}
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
                      {solution.title}
                    </h2>
                    {solution.subtitle && (
                      <h3 className="text-xl font-semibold text-ewss-cyan mb-4">
                        {solution.subtitle}
                      </h3>
                    )}
                    <p className="text-gray-300 text-lg mb-6">
                      {solution.description}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                      {solution.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start">
                          <div className="w-2 h-2 bg-ewss-cyan rounded-full mr-3 mt-2 flex-shrink-0" />
                          <span className="text-gray-400 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                    {solution.enabledBy && (
                      <div className="pt-4 border-t border-ewss-cyan/20">
                        <p className="text-gray-400 text-sm mb-2">Enabled by:</p>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {solution.enabledBy.map((brand, idx) => (
                            <span key={idx} className="px-3 py-1 bg-ewss-cyan/10 text-ewss-cyan text-sm font-semibold rounded-full border border-ewss-cyan/30">
                              {brand}
                            </span>
                          ))}
                        </div>
                        <Link 
                          to="/contact"
                          className="inline-flex items-center px-6 py-3 bg-ewss-cyan text-ewss-navy font-semibold rounded-lg hover:shadow-[0_0_20px_rgba(23,145,255,0.5)] transition-all duration-300 group"
                          data-testid="discuss-service-btn"
                        >
                          Discuss This Service
                          <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-ewss-navy to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="bg-gradient-to-br from-ewss-cyan/10 to-transparent border border-ewss-cyan/30 rounded-2xl p-8 md:p-12 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Need a Custom Solution?
              </h2>
              <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
                Our engineering team can design and implement tailored automation solutions for your specific requirements.
              </p>
              <a
                href="mailto:info@ewss.co.za"
                className="inline-block px-8 py-4 bg-ewss-cyan text-ewss-navy font-semibold rounded-lg hover:shadow-[0_0_30px_rgba(0,229,255,0.5)] transition-all duration-300"
                data-testid="contact-engineering-btn"
              >
                Contact Our Engineering Team
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default Solutions;